import { useCallback, useRef } from 'react';
import type { ClickExpandNarrationTarget, ClickExpandTargetRegistration } from '../narrationEvents';
import type { ActiveNarrationPlan } from '../narrationPlans';

export interface ClickExpandTargetRegistry {
  /** Renderers report their target per block; a stale unregister never removes a newer target. */
  registerClickExpandTarget: ClickExpandTargetRegistration;
  getClickExpandTarget: (blockId: string) => ClickExpandNarrationTarget | null;
  /**
   * The target a Click-Expand plan may act on. Records it on the plan when the
   * plan has none yet; returns `null` once the block registered a different
   * target or none, which ends that plan's effects.
   */
  claimForPlan: (plan: ActiveNarrationPlan) => ClickExpandNarrationTarget | null;
}

/** Mounted Click-Expand renderer targets, keyed by block id, for narration plans to drive. */
export function useClickExpandTargetRegistry(): ClickExpandTargetRegistry {
  const targetsRef = useRef<Map<string, ClickExpandNarrationTarget>>(new Map());

  const registerClickExpandTarget = useCallback<ClickExpandTargetRegistration>((
    blockId,
    target,
    previousTarget,
  ) => {
    if (target) {
      targetsRef.current.set(blockId, target);
      return;
    }
    if (previousTarget && targetsRef.current.get(blockId) === previousTarget) {
      targetsRef.current.delete(blockId);
    }
  }, []);

  const getClickExpandTarget = useCallback(
    (blockId: string): ClickExpandNarrationTarget | null => targetsRef.current.get(blockId) ?? null,
    [],
  );

  const claimForPlan = useCallback((plan: ActiveNarrationPlan): ClickExpandNarrationTarget | null => {
    const registered = targetsRef.current.get(plan.blockId) ?? null;
    if (plan.clickExpandTarget === null) plan.clickExpandTarget = registered;
    if (registered === null || registered !== plan.clickExpandTarget) return null;
    return registered;
  }, []);

  return { registerClickExpandTarget, getClickExpandTarget, claimForPlan };
}
